import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { ArrowLeft, CheckCircle2, Flag, Loader2 } from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { seoHead } from "@/lib/seo";
import { supabase } from "@/integrations/supabase/client";
import { submitContact } from "@/lib/contacts.functions";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { track } from "@/lib/analytics";

export const Route = createFileRoute("/report")({
  head: () =>
    seoHead({
      title: "コンテンツを通報する - KAGEM",
      description:
        "KAGEMで生成された動画について、規約違反の疑いがある場合はこちらから通報できます。URL・スクリーンショット・状況説明のいずれでも受け付けます。",
      path: "/report",
    }),
  component: ReportLanding,
});

const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

function extractGenerationId(input: string): string | null {
  if (!input) return null;
  const m = input.match(UUID_RE);
  return m ? m[0].toLowerCase() : null;
}

const REASON_OPTIONS = [
  { value: "adult", label: "成人向けコンテンツ" },
  { value: "violence", label: "暴力的・有害な内容" },
  { value: "impersonation", label: "なりすまし・有名人の使用" },
  { value: "copyright", label: "著作権侵害" },
  { value: "minor", label: "未成年関連" },
  { value: "hate", label: "ヘイトスピーチ・差別" },
  { value: "other", label: "その他" },
];

const DETAIL_MAX = 2000;

const FormSchema = z.object({
  name: z.string().trim().min(1, "お名前を入力してください").max(100),
  email: z
    .string()
    .trim()
    .email("有効なメールアドレスを入力してください")
    .max(255),
  detail: z
    .string()
    .trim()
    .min(10, "状況を10文字以上で記入してください")
    .max(DETAIL_MAX),
  url: z.string().trim().max(2048).optional(),
});

function ReportLanding() {
  const navigate = useNavigate();
  const submitFn = useServerFn(submitContact);

  const [reasons, setReasons] = useState<string[]>([]);
  const [url, setUrl] = useState("");
  const [detail, setDetail] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const detectedId = useMemo(() => extractGenerationId(url), [url]);

  const toggleReason = (value: string, checked: boolean) => {
    setReasons((prev) =>
      checked
        ? Array.from(new Set([...prev, value]))
        : prev.filter((v) => v !== value),
    );
  };

  // KAGEM内のURL/IDが特定できた場合は構造化フォームへ
  const goStructured = () => {
    if (!detectedId) return;
    navigate({
      to: "/report-form",
      search: { generation_id: detectedId } as never,
    });
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (reasons.length === 0) {
      toast.error("通報理由を1つ以上選択してください");
      return;
    }

    const parsed = FormSchema.safeParse({ name, email, detail, url });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "入力内容を確認してください");
      return;
    }

    const reasonLabels = reasons
      .map((r) => REASON_OPTIONS.find((o) => o.value === r)?.label ?? r)
      .join("、");
    const subject = `[通報] ${reasonLabels}`.slice(0, 200);
    const body = [
      `通報理由: ${reasonLabels}`,
      parsed.data.url ? `対象URL: ${parsed.data.url}` : null,
      detectedId ? `検出された動画ID: ${detectedId}` : null,
      "",
      "詳細:",
      parsed.data.detail,
    ]
      .filter(Boolean)
      .join("\n");

    setBusy(true);
    try {
      const recaptcha_token = await getRecaptchaToken("contact_submit");
      const { data: sessionData } = await supabase.auth.getSession();
      const headers = sessionData.session
        ? { Authorization: `Bearer ${sessionData.session.access_token}` }
        : undefined;
      await submitFn({
        data: {
          name: parsed.data.name,
          email: parsed.data.email,
          subject,
          body,
          recaptcha_token: recaptcha_token ?? undefined,
        },
        headers,
      });
      track("report_submit", { reasons, has_url: !!parsed.data.url });
      setSubmitted(true);
      toast.success("通報を受け付けました");
    } catch (err) {
      console.error(err);
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("recaptcha") || msg.includes("RECAPTCHA")) {
        toast.error(
          "送信内容の検証に失敗しました。ページを再読み込みして再度お試しください。",
        );
      } else {
        toast.error("送信に失敗しました。しばらくしてから再度お試しください。");
      }
    } finally {
      setBusy(false);
    }
  }

  if (submitted) {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-xl">
          <Card className="p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <CheckCircle2 className="h-7 w-7 text-primary" />
            </div>
            <h1 className="text-2xl font-bold">通報を受け付けました</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              内容を確認のうえ、24〜48時間以内に運営が対応します。
              追加の確認が必要な場合は、ご記入いただいたメールアドレス宛にご連絡いたします。
            </p>
            <Button asChild className="mt-6">
              <Link to="/">トップへ戻る</Link>
            </Button>
          </Card>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background px-4 py-12">
      <div className="mx-auto max-w-2xl">
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/">
            <ArrowLeft className="mr-1 h-4 w-4" />
            トップへ戻る
          </Link>
        </Button>

        <Card className="p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10">
              <Flag className="h-5 w-5 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">
              コンテンツを通報する
            </h1>
          </div>

          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            KAGEMで生成された動画について、規約違反の疑いがある場合はこちらから通報できます。
            <strong className="text-foreground">URL・状況説明のいずれか</strong>
            で受け付けます。確認後、運営が24〜48時間以内に対応します。
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-6">
            {/* 通報理由 */}
            <div>
              <Label className="text-sm font-semibold">
                通報理由（1つ以上選択） <span className="text-destructive">*</span>
              </Label>
              <div className="mt-3 space-y-2">
                {REASON_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className="flex cursor-pointer items-center gap-3 rounded-md border bg-background p-3 hover:bg-muted/40"
                  >
                    <Checkbox
                      checked={reasons.includes(opt.value)}
                      onCheckedChange={(c) => toggleReason(opt.value, c === true)}
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* URL */}
            <div>
              <Label htmlFor="url" className="text-sm font-semibold">
                対象のURL（任意）
              </Label>
              <Input
                id="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://... （動画ページのURL、SNS投稿のURL など）"
                className="mt-2"
                autoComplete="off"
                inputMode="url"
                maxLength={2048}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                どこか別のサイト（SNS等）に転載されている場合は、その投稿のURLでも構いません。
              </p>
              {detectedId && (
                <div className="mt-2 flex items-center justify-between rounded-md border border-primary/20 bg-primary/5 p-2.5 text-xs">
                  <span>
                    KAGEMの動画IDを検出しました:{" "}
                    <span className="font-mono">{detectedId.slice(0, 8)}…</span>
                  </span>
                  <Button
                    type="button"
                    variant="link"
                    size="sm"
                    onClick={goStructured}
                    className="h-auto p-0 text-xs"
                  >
                    専用フォームへ →
                  </Button>
                </div>
              )}
            </div>

            {/* 詳細 */}
            <div>
              <Label htmlFor="detail" className="text-sm font-semibold">
                状況の説明 <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="detail"
                value={detail}
                onChange={(e) =>
                  setDetail(e.target.value.slice(0, DETAIL_MAX))
                }
                placeholder="どこで見つけたか、どのような内容か、なぜ問題と感じたかをご記入ください。&#10;スクリーンショットがある場合は、画像のURL（X / Imgur / Google Drive など）を貼り付けてください。"
                className="mt-2 min-h-[140px]"
                maxLength={DETAIL_MAX}
              />
              <p className="mt-1 text-right text-xs text-muted-foreground">
                {detail.length} / {DETAIL_MAX}
              </p>
            </div>

            {/* 連絡先 */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="name" className="text-sm font-semibold">
                  お名前 <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="山田 太郎"
                  className="mt-2"
                  autoComplete="name"
                  maxLength={100}
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-semibold">
                  メールアドレス <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="mt-2"
                  autoComplete="email"
                  maxLength={255}
                />
              </div>
            </div>

            <Button type="submit" className="w-full" size="lg" disabled={busy}>
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  送信中...
                </>
              ) : (
                "通報を送信"
              )}
            </Button>
          </form>

          <div className="mt-8 rounded-lg border bg-muted/40 p-4 text-xs leading-relaxed text-muted-foreground">
            <p className="font-semibold text-foreground">ご注意ください</p>
            <ul className="mt-2 list-inside list-disc space-y-1">
              <li>
                スクリーンショット等のファイルは、SNS投稿や画像共有サービス（Imgur、Google Drive、Dropbox など）にアップロードしてURLを貼り付けてください。
              </li>
              <li>虚偽の通報を繰り返した場合、利用制限の対象となることがあります。</li>
              <li>
                その他のお問い合わせは{" "}
                <Link to="/contact" className="underline hover:text-foreground">
                  お問い合わせフォーム
                </Link>{" "}
                をご利用ください。
              </li>
            </ul>
          </div>
        </Card>
      </div>
    </main>
  );
}
